import { Controller } from "@hotwired/stimulus";

import {
  initializeSanitationRoute,
  getNextDatapointUrl,
} from "../../db/sanitationRoutes.js";
import { scheduledRoutePrefillFromHash } from "./first_step_prefill.js";
import {
  navigateToRouteDocument,
  recoverFromBlockedRouteNavigation,
} from "../../routeNavigation.js";
import { isQuotaError, reportIfQuotaError } from "../../db/quotaSignal.js";

// Connects to data-controller="sanitation-forms--first-step"
export default class extends Controller {
  static targets = [
    "form",
    "submitButton",
    "loadingSpinner",
    "nextIcon",
    "monitorSelect",
    "lotInput",
    "lotCombobox",
    "recoveryMessage",
    "recoveryMessageText",
  ];

  static values = {
    sanitationForm: Object,
    lots: Object,
    routeLauncherPath: String,
    savedOfflineBlockedMessage: String,
    storageFullMessage: String,
  };

  connect() {
    // Optional: Initial checks like ensuring lotsValue is populated
    if (!this.lotsValue || Object.keys(this.lotsValue).length === 0) {
      console.error("Lots data not provided to first_step controller.");
      alert("Error: No se encontraron lotes disponibles.");
      if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = true;
    }
    if (
      !this.sanitationFormValue ||
      Object.keys(this.sanitationFormValue).length === 0
    ) {
      console.error(
        "Sanitation form data not provided to first_step controller."
      );
      alert("Error: No se encontró la información del formulario.");
      if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = true;
    }

    this.applyScheduledRoutePrefill();
  }

  applyScheduledRoutePrefill() {
    const { monitorId, lotId } = scheduledRoutePrefillFromHash(
      window.location.hash
    );

    this.prefillMonitor(monitorId);
    this.prefillLot(lotId);
  }

  prefillMonitor(monitorId) {
    if (!monitorId || !this.hasMonitorSelectTarget) return;

    const option = Array.from(this.monitorSelectTarget.options).find(
      (option) => option.value === monitorId
    );
    if (!option) return;

    this.monitorSelectTarget.value = monitorId;
    this.monitorSelectTarget.dispatchEvent(
      new Event("change", { bubbles: true })
    );
  }

  prefillLot(lotId) {
    if (!lotId || !this.lotsValue[lotId]) return;

    const lotInput = this.lotInput;
    if (!lotInput) return;

    lotInput.value = lotId;
    lotInput.dispatchEvent(new Event("input", { bubbles: true }));
    lotInput.dispatchEvent(new Event("change", { bubbles: true }));

    const combobox = this.lotCombobox;
    if (combobox) combobox.value = this.lotsValue[lotId].name;

    this.markLotOptionSelected(lotId);
  }

  markLotOptionSelected(lotId) {
    const fieldset = this.lotInput?.closest(".hw-combobox");
    if (!fieldset) return;

    fieldset
      .querySelectorAll("[role='option'][aria-selected='true']")
      .forEach((option) => {
        option.removeAttribute("aria-selected");
        option.classList.remove("hw-combobox__option--selected");
      });

    const selectedOption = Array.from(
      fieldset.querySelectorAll("[role='option']")
    ).find((option) => option.dataset.value === lotId);
    if (!selectedOption) return;

    selectedOption.setAttribute("aria-selected", "true");
    selectedOption.classList.add("hw-combobox__option--selected");
    this.lotCombobox?.setAttribute("aria-activedescendant", selectedOption.id);
  }

  get lotInput() {
    if (this.hasLotInputTarget) return this.lotInputTarget;

    return this.formTarget.querySelector('input[name="lot_id"][type="hidden"]');
  }

  get lotCombobox() {
    if (this.hasLotComboboxTarget) return this.lotComboboxTarget;

    return this.lotInput
      ?.closest(".hw-combobox")
      ?.querySelector(".hw-combobox__input");
  }

  setLoadingState(isLoading) {
    if (
      !this.hasSubmitButtonTarget ||
      !this.hasNextIconTarget ||
      !this.hasLoadingSpinnerTarget
    )
      return;
    this.submitButtonTarget.disabled = isLoading;
    this.nextIconTarget.classList.toggle("hidden", isLoading);
    this.loadingSpinnerTarget.classList.toggle("hidden", !isLoading);
  }

  async submit(e) {
    e.preventDefault();
    if (!this.hasSubmitButtonTarget || !this.hasMonitorSelectTarget) return;

    if (this.submitButtonTarget.disabled) return;

    try {
      this.setLoadingState(true);

      const lotIdInput = document.querySelector(
        'input[name="lot_id"][type="hidden"]'
      );

      if (!lotIdInput || !lotIdInput.value) {
        throw new Error("Lote no seleccionado.");
      }
      if (!this.monitorSelectTarget.value) {
        throw new Error("Monitor no seleccionado.");
      }

      const lotId = lotIdInput.value;
      const monitorId = this.monitorSelectTarget.value;
      const monitorName = this.monitorSelectTarget.selectedOptions[0].innerText;
      const lot = this.lotsValue[lotId];

      if (!lot) {
        throw new Error(`Información del lote ${lotId} no encontrada.`);
      }

      const routeId = await initializeSanitationRoute({
        lot,
        monitorId,
        monitorName,
        sanitationForm: this.sanitationFormValue,
      });

      // Pass default intent for initial step determination
      const nextStepUrl = await getNextDatapointUrl(routeId, {
        intent: "next_step",
      });
      await this.navigateAfterLocalSave(nextStepUrl);
      // No need to setLoadingState(false) as page navigates away
    } catch (error) {
      this.setLoadingState(false); // Reset loading state on error
      console.error("Error submitting first step:", error);
      reportIfQuotaError(error);
      if (isQuotaError(error)) {
        // A raw QuotaExceededError is meaningless to a monitor — tell them
        // what happened and what to do about it.
        alert(
          this.storageFullMessageValue ||
            "El dispositivo no tiene espacio, así que no se pudo iniciar el recorrido. Sube y elimina recorridos pendientes o libera espacio en el dispositivo, y vuelve a intentarlo.",
        );
      } else {
        alert(`Error al iniciar el recorrido: ${error.message}`);
      }
    }
  }

  async navigateAfterLocalSave(nextStepUrl) {
    const result = await navigateToRouteDocument(nextStepUrl);
    if (result.allowed) return result;

    await recoverFromBlockedRouteNavigation(this, result, {
      warnMessage:
        "Local sanitation route started but next screen is missing offline",
      disableControls: () => {
        this.setLoadingState(false);
        if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = true;
      },
    });
    return result;
  }
}
